namespace $.$$ {

	type $shm_hitalama_process_monitor_node = {
		id: string
		parent: string
		children: readonly string[]
	}

	export class $shm_hitalama_process_monitor extends $.$shm_hitalama_process_monitor {

		@ $mol_mem
		spread( next?: string ) {
			if( next !== undefined ) return next
			return ''
		}

		@ $mol_mem
		spread_ids() {
			return this.$.$shm_hitalama_process.keys().toSorted( $mol_compare_text() )
		}

		key( id: string ) {
			return id
		}

		Spread( id: string ) {
			return this.Page( id )
		}

		spread_current() {
			const spread = this.spread()
			if( !spread ) return null
			if( !this.spread_ids().includes( spread ) ) return null
			return this.Spread( spread )
		}

		@ $mol_mem
		override pages() {
			const spread = this.spread_current()
			return [
				this.Menu(),
				... spread
					? spread instanceof $mol_book2
						? spread.pages_deep()
						: [ spread ]
					: [],
			]
		}

		spread_title( id: string ) {
			const process = this.$.$shm_hitalama_process.last_by_key( id )
			if( !process ) return id

			return this.process_title( process )
		}

		@ $mol_mem
		spread_ids_filtered() {
			const include = this.menu_filter().trim().toLowerCase()
			const exclude = this.exclude_substr().trim().toLowerCase()

			return this.spread_ids().filter( id => {
				const title = this.spread_title( id ).toLowerCase()
				if( include && !title.includes( include ) ) return false
				if( exclude && title.includes( exclude ) ) return false
				return true
			} )
		}

		process_key( process: $shm_hitalama_process< any > ) {
			return this.$.$shm_hitalama_process.key_of( process )
		}

		process_parent( id: string ) {
			const parent_key = this.$.$shm_hitalama_process.parent_key( id )
			if( parent_key && parent_key !== id ) return parent_key

			const process = this.$.$shm_hitalama_process.last_by_key( id )
			if( !process?.parent ) return ''

			return this.process_key( process.parent )
		}

		@ $mol_mem
		process_nodes() {
			const ids = this.spread_ids_filtered()
			const known = new Set( ids )
			const children = new Map< string, string[] >()
			const parent_by_id = new Map< string, string >()

			for( const id of ids ) {
				const parent = this.process_parent( id )
				const root_parent = known.has( parent ) ? parent : ''

				parent_by_id.set( id, root_parent )

				const list = children.get( root_parent ) ?? []
				list.push( id )
				children.set( root_parent, list )
			}

			for( const [ parent, list ] of children ) {
				if( this.$.$shm_hitalama_process.last_by_key( parent )?.data().started_tick === undefined ) continue
				list.sort( ( a, b )=> {
					const left = this.$.$shm_hitalama_process.last_by_key( a )?.data()
					const right = this.$.$shm_hitalama_process.last_by_key( b )?.data()
					if( left?.display_order !== undefined || right?.display_order !== undefined ) {
						const order = ( left?.display_order ?? Infinity ) - ( right?.display_order ?? Infinity )
						if( order ) return order
					}
					return ( left?.started_tick ?? 0 ) - ( right?.started_tick ?? 0 )
				} )
			}
			return ids.map( id => ({
				id,
				parent: parent_by_id.get( id ) ?? '',
				children: children.get( id ) ?? [],
			}) ) as readonly $shm_hitalama_process_monitor_node[]
		}

		process_node( id: string ) {
			return this.process_nodes().find( node => node.id === id ) ?? null
		}

		@ $mol_mem
		root_process_ids() {
			const roots = this.process_nodes()
				.filter( node => !node.parent )
				.map( node => node.id )
			const observed = roots.filter( id => this.$.$shm_hitalama_process.last_by_key( id )?.data().started_tick !== undefined )
			observed.sort( ( a, b )=> this.$.$shm_hitalama_process.last_by_key( b )!.data().started_tick! - this.$.$shm_hitalama_process.last_by_key( a )!.data().started_tick! )
			return [ ...observed, ...roots.filter( id => !observed.includes( id ) ) ]
		}

		@ $mol_mem_key
		process_expanded( id: string, next?: boolean ): boolean {
			if( next !== undefined ) return next
			return this.process_node( id )?.parent === ''
		}

		process_children( id: string ) { return this.process_node( id )?.children ?? [] }

		detail_current( id: string ) {
			return this.spread() === id
		}

		detail_click( id: string, event?: Event ) {
			event?.preventDefault()
			this.spread( this.spread() === id ? '' : id )
			return event
		}

		detail_close( id: string, next?: unknown ) {
			if( next !== undefined && this.spread() === id ) this.spread( '' )
			return null
		}

		widget_click( id: string, event?: Event ) {
			event?.preventDefault()
			return event
		}

		process_status( id: string ) {
			return this.$.$shm_hitalama_process.last_by_key( id )?.data().status ?? 'pending'
		}

		process_status_color( id: string ) {
			switch( this.process_status( id ) ) {
				case 'done': return `${ $shm_theme.success }`
				case 'failed': return `${ $shm_theme.error }`
				case 'running': return `${ $shm_theme.active }`
				default: return `${ $mol_theme.shade }`
			}
		}

		process_status_tint( id: string ) {
			return `color-mix( in srgb, ${ this.process_status_color( id ) } 16%, transparent )`
		}

		process_duration_width( id: string ) {
			return this.duration_group_width( this.process_node( id )?.parent ?? '' )
		}

		@ $mol_mem_key
		duration_group_width( parent: string ) {
			const siblings = parent ? this.process_children( parent ) : this.root_process_ids()
			return `${ siblings.reduce( ( max, id )=> Math.max( max, this.process_duration( id ).length ), 1 ) }ch`
		}

		process_duration( id: string ) {
			const process = this.$.$shm_hitalama_process.last_by_key( id )
			if( !process ) return '-'

			if( process.data().status === 'running' ) this.$.$mol_state_time.now( 250 )

			const duration = process.duration()
			if( duration < 1000 ) return `${ Math.round( duration ) } ms`
			if( duration < 60000 ) return `${ ( duration / 1000 ).toFixed( 1 ) } s`

			const minutes = Math.floor( duration / 60000 )
			const seconds = Math.floor(( duration - minutes * 60000 ) / 1000 )
			return `${ minutes } m ${ seconds } s`
		}

		process_title( process: $shm_hitalama_process< any > ) {
			if( process.data().label ) return process.data().label!
			const args = process.args.map( arg => this.short_value( arg ) ).join( ', ' )
			return `${ this.host_title( process.host ) }${ process.method_name }(${ args })`
		}

		host_title( host: object | undefined ) {
			if( !host ) return ''

			const full = `${ host }.`
			const widgets = [ ... full.matchAll( /(?:^|\.)(Widget<[^>]+>)(?=\.|$)/g ) ]
			const widget = widgets[ widgets.length - 1 ]
			if( widget ) return `${ widget[1] }.`

			return ''
		}

		short_value( value: unknown ) {
			if( typeof value === 'string' ) return JSON.stringify( value.length > 24 ? value.slice( 0, 21 ) + '...' : value )
			if( typeof value === 'number' || typeof value === 'boolean' || value === null ) return String( value )
			if( value === undefined ) return 'undefined'

			const title = ( value as { constructor?: { name?: string } } )?.constructor?.name
			return title && title !== 'Object' ? title : '{...}'
		}

	}

}
